/**
 * Catálogo tab — área dedicada pra entidades comerciais estruturadas.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 3).
 *
 * Coexiste com Guidelines (RAG) — guidelines pra narrativas livres
 * (manuais, FAQs, políticas), catálogo pra produtos/serviços
 * estruturados com schema fixo.
 *
 * Layout:
 *   - Filters: categoria, tipo, status, busca
 *   - Botão "+ Novo item" + "Testar no chat"
 *   - Lista de cards: name, badges, status indexado, ações
 *   - Modal form pra criar/editar
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  listCatalogItems,
  createCatalogItem,
  updateCatalogItem,
  deleteCatalogItem,
  deleteAllCatalogItems,
  reindexAllCatalogItems,
} from '../../services/catalog';
import type {
  CatalogItem,
  CatalogItemStatus,
  CatalogItemType,
  CreateCatalogItemRequest,
} from '../../types';
import { CatalogItemForm } from './CatalogItemForm';
import { CatalogAskModal } from './CatalogAskModal';
import { CatalogImportCsvModal } from './CatalogImportCsvModal';
import { CatalogImportUrlModal } from './CatalogImportUrlModal';

interface Props {
  canEdit: boolean;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  width: 'min(840px, 92vw)',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
};

const filterInput: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 13,
  background: '#fff',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#475569',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};
const btnDanger: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#b91c1c',
  border: '1px solid #fecaca',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};

const Badge: React.FC<{ children: React.ReactNode; color?: string; bg?: string }> = ({ children, color, bg }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: bg || '#e0e7ff',
      color: color || '#4338ca',
    }}
  >
    {children}
  </span>
);

export const CatalogTab: React.FC<Props> = ({ canEdit }) => {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterType, setFilterType] = useState<CatalogItemType | ''>('');
  const [filterStatus, setFilterStatus] = useState<CatalogItemStatus | ''>('');
  const [search, setSearch] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<CatalogItem | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const [showAsk, setShowAsk] = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [showImportUrl, setShowImportUrl] = useState(false);

  const [showDeleteAll, setShowDeleteAll] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [bulkBusy, setBulkBusy] = useState<'delete' | 'reindex' | null>(null);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listCatalogItems();
      setItems(list);
    } catch (err) {
      setError((err as Error).message || 'Erro ao carregar catálogo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filterType && item.type !== filterType) return false;
      if (filterStatus && item.status !== filterStatus) return false;
      if (q) {
        const hay = `${item.name} ${item.categoryLabel} ${item.descriptionShort}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, filterType, filterStatus, search]);

  const categoriesCount = useMemo(() => {
    const set = new Set(items.map((i) => i.categoryLabel));
    return set.size;
  }, [items]);

  const handleCreate = async (payload: CreateCatalogItemRequest) => {
    setSaving(true);
    try {
      await createCatalogItem(payload);
      setShowForm(false);
      setEditingItem(undefined);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload: CreateCatalogItemRequest) => {
    if (!editingItem) return;
    setSaving(true);
    try {
      await updateCatalogItem(editingItem.productId, payload);
      setShowForm(false);
      setEditingItem(undefined);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAll = async () => {
    setBulkBusy('delete');
    setBulkResult(null);
    try {
      const result = await deleteAllCatalogItems();
      const note = result.pineconeOk
        ? `${result.deleted} items + vetores Pinecone removidos.`
        : `${result.deleted} items removidos do banco. Atenção: erro no Pinecone (${result.pineconeError}).`;
      setBulkResult(note);
      setShowDeleteAll(false);
      setDeleteConfirmText('');
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBulkBusy(null);
    }
  };

  const handleReindexAll = async () => {
    if (
      !window.confirm(
        `Re-disparar indexação de até 15 items pendentes (~20-30s). Se houver mais, é só clicar de novo após terminar.`,
      )
    ) {
      return;
    }
    setBulkBusy('reindex');
    setBulkResult(null);
    try {
      const result = await reindexAllCatalogItems();
      const parts = [`${result.succeeded}/${result.processed} indexados`];
      if (result.failed > 0) parts.push(`${result.failed} falhas`);
      if (result.remaining > 0) parts.push(`${result.remaining} aguardando (clique de novo)`);
      setBulkResult(parts.join(' · '));
      await reload();
    } catch (err) {
      // Mesmo com Failed to fetch, o backend pode ter completado a indexação
      // (API Gateway timeout 30s não interrompe a Lambda). Recarrega lista
      // pra refletir o que efetivamente foi indexado.
      setBulkResult(
        'Frontend desconectou antes do backend responder, mas a indexação pode ter sido concluída. Recarregando lista…',
      );
      setTimeout(() => void reload(), 5000);
    } finally {
      setBulkBusy(null);
    }
  };

  const handleDelete = async (item: CatalogItem) => {
    if (!window.confirm(`Excluir "${item.name}" do catálogo? Vetores no Pinecone serão removidos.`)) {
      return;
    }
    try {
      await deleteCatalogItem(item.productId);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="ai-subtab-content">
      <section className="settings-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0' }}>📦 Catálogo</h2>
            <p className="section-description" style={{ margin: 0 }}>
              Produtos e serviços estruturados com indexação RAG por seção lógica. {items.length}{' '}
              {items.length === 1 ? 'item' : 'items'}, {categoriesCount}{' '}
              {categoriesCount === 1 ? 'categoria' : 'categorias'}.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setShowAsk(true)} style={btnSecondary} disabled={items.length === 0}>
              🔍 Testar no chat
            </button>
            {canEdit && (
              <>
                {items.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={handleReindexAll}
                      style={btnSecondary}
                      disabled={bulkBusy !== null}
                      title="Re-disparar indexação Pinecone de todos items"
                    >
                      {bulkBusy === 'reindex' ? '⏳ Reindexando…' : '🔄 Reindexar todos'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteAll(true)}
                      style={btnDanger}
                      disabled={bulkBusy !== null}
                      title="Apaga TODOS items + vetores Pinecone"
                    >
                      🗑️ Excluir todos
                    </button>
                  </>
                )}
                <button type="button" onClick={() => setShowImportCsv(true)} style={btnSecondary}>
                  📥 Importar CSV
                </button>
                <button type="button" onClick={() => setShowImportUrl(true)} style={btnSecondary}>
                  🌐 Importar URL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingItem(undefined);
                    setShowForm(true);
                  }}
                  style={btnPrimary}
                >
                  + Novo item
                </button>
              </>
            )}
          </div>
        </div>

        {bulkResult && (
          <div
            style={{
              marginTop: 8,
              padding: '8px 12px',
              background: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: 6,
              color: '#065f46',
              fontSize: 13,
            }}
          >
            ✓ {bulkResult}{' '}
            <button
              type="button"
              onClick={() => setBulkResult(null)}
              style={{ background: 'none', border: 0, color: '#065f46', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            margin: '16px 0',
            padding: 12,
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
          }}
        >
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as CatalogItemType | '')}
            style={filterInput}
          >
            <option value="">Tipo (todos)</option>
            <option value="produto">Produto</option>
            <option value="servico">Serviço</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as CatalogItemStatus | '')}
            style={filterInput}
          >
            <option value="">Status (todos)</option>
            <option value="active">Ativo</option>
            <option value="draft">Rascunho</option>
            <option value="inactive">Inativo</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Buscar nome, categoria, descrição…"
            style={{ ...filterInput, flex: 1 }}
          />
        </div>

        {loading && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Carregando…</div>}

        {error && (
          <div
            style={{
              padding: 12,
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            {items.length === 0
              ? 'Nenhum item no catálogo. Clique em "+ Novo item" pra começar.'
              : 'Nenhum item bate com os filtros.'}
          </div>
        )}

        {/* List */}
        {!loading &&
          filteredItems.map((item) => (
            <div
              key={item.productId}
              style={{
                padding: 14,
                marginBottom: 8,
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: '0 0 4px 0', fontSize: 15, fontWeight: 600 }}>{item.name}</h4>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Badge>{item.categoryLabel}</Badge>
                    <Badge bg="#f0fdf4" color="#15803d">
                      {item.type === 'produto' ? 'Produto' : 'Serviço'}
                    </Badge>
                    {item.status !== 'active' && (
                      <Badge bg={item.status === 'draft' ? '#fef3c7' : '#fee2e2'} color={item.status === 'draft' ? '#92400e' : '#991b1b'}>
                        {item.status === 'draft' ? 'Rascunho' : 'Inativo'}
                      </Badge>
                    )}
                    {item.pineconeIndexedAt ? (
                      <Badge bg="#dcfce7" color="#166534">
                        ✓ Indexado
                      </Badge>
                    ) : (
                      <Badge bg="#fef3c7" color="#92400e">
                        ⏳ Indexando…
                      </Badge>
                    )}
                  </div>
                </div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingItem(item);
                        setShowForm(true);
                      }}
                      style={btnSecondary}
                    >
                      Editar
                    </button>
                    <button type="button" onClick={() => void handleDelete(item)} style={btnDanger}>
                      Excluir
                    </button>
                  </div>
                )}
              </div>
              {item.descriptionShort && (
                <p style={{ margin: '6px 0 0 0', fontSize: 13, color: '#475569' }}>{item.descriptionShort}</p>
              )}
            </div>
          ))}

        {/* Modal: form */}
        {showForm && (
          <div style={overlay} onClick={() => !saving && setShowForm(false)}>
            <div style={modal} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                  {editingItem ? `Editar: ${editingItem.name}` : 'Novo item de catálogo'}
                </h3>
                <button
                  type="button"
                  onClick={() => !saving && setShowForm(false)}
                  style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
                  disabled={saving}
                >
                  ×
                </button>
              </div>
              <CatalogItemForm
                initial={editingItem}
                saving={saving}
                onCancel={() => setShowForm(false)}
                onSubmit={editingItem ? handleUpdate : handleCreate}
              />
            </div>
          </div>
        )}

        {/* Modal: ask */}
        {showAsk && <CatalogAskModal onClose={() => setShowAsk(false)} />}

        {/* Modal: import CSV */}
        {showImportCsv && (
          <CatalogImportCsvModal
            onClose={() => setShowImportCsv(false)}
            onImported={() => void reload()}
          />
        )}

        {/* Modal: import URL */}
        {showImportUrl && (
          <CatalogImportUrlModal
            onClose={() => setShowImportUrl(false)}
            onCreated={() => void reload()}
          />
        )}

        {/* Modal: confirmar exclusão total */}
        {showDeleteAll && (
          <div style={overlay} onClick={() => bulkBusy === null && setShowDeleteAll(false)}>
            <div style={{ ...modal, width: 'min(520px, 92vw)' }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 12px 0', color: '#b91c1c' }}>
                🗑️ Excluir todo o catálogo
              </h3>
              <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.5 }}>
                Vai apagar <strong>{items.length} items</strong> do banco e remover{' '}
                <strong>todos os vetores Pinecone</strong> da organização. A operação é
                <strong> irreversível</strong>.
              </p>
              <p style={{ color: '#475569', fontSize: 13, marginTop: 12 }}>
                Pra confirmar, digite <code style={{ background: '#fef2f2', padding: '2px 6px', borderRadius: 4 }}>EXCLUIR</code> abaixo:
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="EXCLUIR"
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #fecaca',
                  borderRadius: 6,
                  fontSize: 14,
                  marginTop: 4,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteAll(false);
                    setDeleteConfirmText('');
                  }}
                  style={btnSecondary}
                  disabled={bulkBusy !== null}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAll}
                  disabled={deleteConfirmText !== 'EXCLUIR' || bulkBusy !== null}
                  style={{
                    ...btnPrimary,
                    background: deleteConfirmText === 'EXCLUIR' ? '#b91c1c' : '#fca5a5',
                    cursor: deleteConfirmText === 'EXCLUIR' && bulkBusy === null ? 'pointer' : 'not-allowed',
                  }}
                >
                  {bulkBusy === 'delete' ? '⏳ Excluindo…' : 'Excluir tudo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CatalogTab;
